import { Indicator } from 'utils/models';
import { WeightIndicator } from 'models/weight';
import { IMPERIAL_UNIT_COUNTRIES, HeightUnit, UnitType, WeightUnit } from 'models/generic';
import { MeasurementHeightUnitsProps, MeasurementWeightUnitsProps } from 'models/general';
import { CurrentUserData } from 'models/user';
import { EmptyParametersError } from 'services/errors/EmptyParametersError';
import {
  RuleParameter,
  RuleParameters,
  RuleParametersDefaults,
  RuleTimeFrames,
  TimeFramesInSeconds,
} from 'models/rules';
import { ReportOutcomeTag } from './models';
import { capitalizeFirstLetter } from './general';
import { isProduction } from './app';

export const BMI_VALUES = {
  underweight: 18.5,
  normal: 25,
  overweight: 30,
};

export function getReportIndicator(tags: string[]) {
  if (tags?.includes(ReportOutcomeTag.Normal)) return Indicator.Normal;
  if (tags?.includes(ReportOutcomeTag.Warning)) return Indicator.Warning;
  if (tags?.includes(ReportOutcomeTag.Urgent)) return Indicator.Urgent;
  if (tags?.includes(ReportOutcomeTag.Inconclusive)) return Indicator.Inconclusive;
  if (tags?.includes(ReportOutcomeTag.Quality)) return Indicator.Quality;
  if (tags?.includes(ReportOutcomeTag.NoResult)) return Indicator.NoResult;
  return Indicator.NoResult;
}

export const capitalize = (value?: string) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const calculateAverage = (arr: number[]) => arr.reduce((p, c) => p + c, 0) / arr.length;

export const listQueryParameterValues = (values: string, mapFn: (value: string) => string) => {
  return values.split(',').map(mapFn).join(',');
};

export const createFileUrlFromBuffer = (bufferke: Buffer) =>
  URL.createObjectURL(new Blob([new Uint8Array(bufferke)], { type: 'application/pdf' }));

export function roundToTwoDecimals(value: number) {
  const roundedNumber = value.toFixed(2);
  const trimmedNumber = roundedNumber.replace(/\.?0+$/, '');
  return Number(trimmedNumber);
}

export const removeWhiteSpace = (text: string) => text.replaceAll(' ', '');

export function replaceNewlineWithBreak(text: string) {
  return text.replace(/\n/g, '<br />');
}

const camelToSnakeCase = (key: string) => {
  const result = key.replace(/([A-Z])/g, ' $1');
  return result.split(' ').join('_').toLowerCase();
};

export const snakeCaseKeys = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map((v) => snakeCaseKeys(v));
  }
  if (obj != null && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [camelToSnakeCase(key)]: snakeCaseKeys(obj[key]),
      }),
      {},
    );
  }
  return obj;
};

export const showFullName = (obj: any): string => {
  if (!obj) return '???';
  return `${obj?.lastName} ${obj?.firstName}`;
};

export const showClass = (className: string, condition: boolean) => (condition ? className : undefined);

export const getMeasurementSystemUnitByCountry = (country?: string) => {
  if (!country) throw new EmptyParametersError();

  if (IMPERIAL_UNIT_COUNTRIES.includes(country)) {
    return UnitType.Imperial;
  }
  return UnitType.Metric;
};

export const displayWeight = (metricValue: number, unit: UnitType) => {
  if (unit === UnitType.Imperial) return Number((metricValue * 2.20462).toFixed(2));
  return metricValue;
};

export const calculateWeightToMetric = (value: number, unit: UnitType) => {
  if (unit === UnitType.Imperial) return Number((value / 2.20462).toFixed(2));
  return value;
};

export const calculateHeight = (value: number, unit: UnitType) => {
  if (unit === UnitType.Imperial) return Number((value / 2.54).toFixed(2));
  return value;
};

export const getWeightUnits = (unit: UnitType) => {
  if (unit === UnitType.Imperial) return WeightUnit.Pounds;
  return WeightUnit.Kilograms;
};

export const getHeightUnits = (unit: UnitType) => {
  if (unit === UnitType.Imperial) return HeightUnit.Inches;
  return HeightUnit.Centimeters;
};

export const calculateBmiIndicator = (value: number) => {
  if (!value) return WeightIndicator.NoResult;

  if (value < BMI_VALUES.underweight) {
    return WeightIndicator.Underweight;
  }
  if (value >= BMI_VALUES.underweight && value < BMI_VALUES.normal) {
    return WeightIndicator.Normal;
  }
  if (value >= BMI_VALUES.normal && value < BMI_VALUES.overweight) {
    return WeightIndicator.Overweight;
  }
  return WeightIndicator.Obesity;
};

export const displayMeasurementWeightUnitsLabel = ({ weight, unitSystem }: MeasurementWeightUnitsProps) => {
  if (!weight || !unitSystem) return '-';
  return `${displayWeight(weight, unitSystem)}${getWeightUnits(unitSystem)}`;
};

export const displayMeasurementHeightUnitsLabel = ({ height, unitSystem }: MeasurementHeightUnitsProps) => {
  if (!height || !unitSystem) return '-';
  return `${calculateHeight(height, unitSystem)}${getHeightUnits(unitSystem)}`;
};

export const getUserMeasurementSystem = (user?: CurrentUserData): UnitType => {
  if (user && user.userConfiguration?.data?.measurementSystem) {
    return capitalizeFirstLetter(user.userConfiguration?.data?.measurementSystem) as UnitType;
  }
  return getMeasurementSystemUnitByCountry(user?.profile?.country);
};

export const getUserPhysicianIdentifier = (user?: CurrentUserData): string | undefined => {
  return user?.userConfiguration?.data?.physicianIdentifier;
};

export const getRuleParameterDefaults = (parameter: RuleParameter, unitType: UnitType) => {
  if (parameter === RuleParameters.weight) {
    return RuleParametersDefaults.find((value) => value.parameter === parameter && value.unitType === unitType);
  }
  return RuleParametersDefaults.find((value) => value.parameter === parameter);
};

export const secondsToTimeWindow = (seconds: number): { value: number; unit: string } => {
  if (seconds % TimeFramesInSeconds.days === 0) {
    return { value: seconds / TimeFramesInSeconds.days, unit: RuleTimeFrames.days };
  }
  if (seconds % TimeFramesInSeconds.hours === 0) {
    return { value: seconds / TimeFramesInSeconds.hours, unit: RuleTimeFrames.hours };
  }
  if (!isProduction && seconds % TimeFramesInSeconds.minutes === 0) {
    return { value: seconds / TimeFramesInSeconds.minutes, unit: RuleTimeFrames.minutes };
  }
  return { value: 0, unit: RuleTimeFrames.hours };
};

export const sanitizeSecret = (code: string) => {
  return code.replace(/\s/g, '');
};
